import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "../api/client";
import { ConfirmDialog } from "../components/Dialog";
import { ErrorNote } from "../components/ErrorNote";
import { conflictMessage, useCampaign, useDraft } from "./context";
import { EntryPlay, playFields } from "./EntryPlay";
import { Markdown } from "./Markdown";
import { MaterialProvider, MaterialToggle, SceneMaterial } from "./material";
import { AttachmentButton, CampaignError, FieldValue, SecretBadge, useAction, useEntries, useEntry, VisibilityToggle } from "./parts";
import { RevealButton } from "./play";
import type { CampaignEntry, CampaignField, CampaignFieldSpec, CampaignSection, Visibility } from "./types";

/** Largest attachment the server accepts (F15); checked before uploading, so a large file is not sent in vain. */
export const maxAttachmentBytes = 20 * 1024 * 1024;

export function EntryPage() {
  const { entryId = "" } = useParams();
  const entry = useEntry(entryId);
  if (entry.isLoading) return <p className="muted">Lade …</p>;
  if (!entry.data) return <p className="error">{entry.error instanceof ApiError && entry.error.status === 404 ? "Eintrag nicht gefunden." : entry.error?.message}</p>;
  return <EntryView entry={entry.data} />;
}

/** An entry with all the view sees of it; embedded on another page, e.g. the campaign on its start page, without its head. */
export function EntryView({ entry, embedded = false }: { entry: CampaignEntry; embedded?: boolean }) {
  const campaign = useCampaign();
  const kind = campaign.kind(entry.kind);
  return (
    <article className="stack entry" aria-label={entry.title}>
      {!embedded && <EntryHead entry={entry} />}
      {campaign.mode === "normal" && entry.visibility === "secret" && (
        <p className="notice material-notice">
          {entry.seenByParts
            ? `Szenenmaterial: Von diesem geheimen Eintrag siehst du nur, was gerade als Material aktiv ist (${campaign.term("scene")}).`
            : "Szenenmaterial: Dieser Eintrag ist geheim."}{" "}
          {campaign.term("player")} sehen davon nichts, bis du es aufdeckst.
        </p>
      )}
      <MaterialProvider entry={entry} off={embedded}>
        {!embedded && <EntryPlay entry={entry} />}
        {(entry.fields.length > 0 || (campaign.canWrite && (kind?.fields.length ?? 0) > 0)) && <EntryFields entry={entry} />}
        {entry.kind === "scene" && <SceneMaterial scene={entry} />}
        {entry.sections.map((section, index) => (
          <SectionCard key={section.id} entry={entry} section={section} index={index} count={entry.sections.length} />
        ))}
        {campaign.canWrite && <NewSection entry={entry} />}
        <EntryLinks entry={entry} />
        {entry.backlinks.length > 0 && (
          <section className="card">
            <h2>Erwähnt in</h2>
            <ul className="plain-list" aria-label="Rückverweise">
              {entry.backlinks.map((b) => (
                <li key={b.sectionId}>
                  <Link to={campaign.entryPath(b.entryId)}>{b.title}</Link> <span className="muted small">· {b.heading}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <EntryAttachments entry={entry} />
      </MaterialProvider>
    </article>
  );
}

function splitList(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function EntryHead({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const navigate = useNavigate();
  const kind = campaign.kind(entry.kind);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [title, setTitle] = useState(entry.title);
  const [aliases, setAliases] = useState(entry.aliases.join(", "));
  const [tags, setTags] = useState(entry.tags.join(", "));
  const action = useAction();
  useDraft(editing && (title !== entry.title || aliases !== entry.aliases.join(", ") || tags !== entry.tags.join(", ")));

  // The form starts from the entry as it is now, after an undo or a change elsewhere, never from an earlier or discarded input.
  const startEditing = () => {
    setTitle(entry.title);
    setAliases(entry.aliases.join(", "));
    setTags(entry.tags.join(", "));
    action.clear();
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await action.run(() =>
      campaign.apply({ type: "campaign.entry.update", payload: { id: entry.id, title, aliases: splitList(aliases), tags: splitList(tags) } }),
    );
    if (ok) setEditing(false);
  };

  return (
    <header className="entry-head">
      {!editing ? (
        <>
          <div className="entry-title">
            <h1>{entry.title}</h1>
            <p className="muted small">
              {campaign.kindLabel(entry.kind)}
              {entry.scope === "world" && <> · {campaign.term("world")}</>}
              {entry.aliases.length > 0 && <> · auch: {entry.aliases.join(", ")}</>}
            </p>
            {entry.tags.length > 0 && (
              <p className="tags">
                {entry.tags.map((t) =>
                  // A tag of a secret entry stays out of the address and so out of the history.
                  entry.visibility === "secret" ? (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ) : (
                    <Link key={t} className="tag" to={`/k/${campaign.id}/eintraege?schlagwort=${encodeURIComponent(t)}`}>
                      {t}
                    </Link>
                  ),
                )}
              </p>
            )}
          </div>
          <div className="actions">
            {kind?.creatable ? (
              <VisibilityToggle
                visibility={entry.visibility}
                label="Eintrag"
                onChange={(visibility) => action.run(() => campaign.apply({ type: "campaign.visibility", payload: { target: "entry", id: entry.id, visibility } }))}
              />
            ) : (
              <SecretBadge visibility={entry.visibility} />
            )}
            {campaign.canWrite && entry.visibility === "secret" && kind?.creatable && (campaign.mode === "dossier" || !entry.seenByParts) && (
              <RevealButton reveal={{ target: "entry", id: entry.id, label: `„${entry.title}“`, entryTitle: entry.title }} />
            )}
            {campaign.canWrite && (
              <button type="button" className="button secondary" onClick={startEditing}>
                Bearbeiten
              </button>
            )}
            {campaign.canWrite && kind?.creatable && (
              <button type="button" className="button secondary" onClick={() => setDeleting(true)}>
                Löschen
              </button>
            )}
          </div>
        </>
      ) : (
        <form className="card stack" onSubmit={(e) => void save(e)}>
          <label className="field">
            <span>Titel</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          </label>
          <label className="field">
            <span>Aliase (mit Komma getrennt)</span>
            <input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </label>
          <label className="field">
            <span>Schlagworte (mit Komma getrennt)</span>
            <input value={tags} onChange={(e) => setTags(e.target.value)} />
          </label>
          <div className="actions">
            <button type="submit" className="button primary" disabled={action.busy}>
              Speichern
            </button>
            <button type="button" className="button secondary" onClick={() => setEditing(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
      {deleting && (
        <ConfirmDialog
          title="Eintrag löschen"
          action="Löschen"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await campaign.apply({ type: "campaign.entry.delete", payload: { id: entry.id } });
            navigate(`/k/${campaign.id}/eintraege`);
          }}
        >
          <p>„{entry.title}“ wird gelöscht. Rückgängig holt den Eintrag zurück.</p>
        </ConfirmDialog>
      )}
    </header>
  );
}

/** The fields the view may set: visible stored fields, and unset ones whose default it sees. */
function editableFields(entry: CampaignEntry, specs: CampaignFieldSpec[], dossier: boolean): CampaignFieldSpec[] {
  const managed = playFields[entry.kind] ?? [];
  return specs.filter((s) => !managed.includes(s.key) && (entry.fields.some((f) => f.key === s.key) || dossier || s.defaultVisibility === "known"));
}

function EntryFields({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const specs = campaign.kind(entry.kind)?.fields ?? [];
  const [editing, setEditing] = useState(false);
  const action = useAction();
  const shown = specs.map((s) => entry.fields.find((f) => f.key === s.key)).filter((f): f is CampaignField => !!f && f.value != null);
  if (editing) return <FieldsForm entry={entry} specs={editableFields(entry, specs, campaign.mode === "dossier")} onDone={() => setEditing(false)} />;
  return (
    <section className="card">
      <div className="section-head">
        <h2 className="visually-hidden">Felder</h2>
        {campaign.canWrite && (
          <button type="button" className="link" onClick={() => setEditing(true)}>
            Felder bearbeiten
          </button>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="muted small">Keine Angaben.</p>
      ) : (
        <dl className="fields">
          {shown.map((f) => (
            <div key={f.key} className="field-row">
              <dt>
                {f.label.de}{" "}
                <VisibilityToggle
                  visibility={f.visibility}
                  label={f.label.de}
                  onChange={(visibility) =>
                    action.run(() => campaign.apply({ type: "campaign.visibility", payload: { target: "field", id: entry.id, field: f.key, visibility } }))
                  }
                />{" "}
                {campaign.canWrite && f.visibility === "secret" && (
                  <RevealButton
                    reveal={{ target: "field", id: entry.id, field: f.key, label: `Feld „${f.label.de}“`, entryTitle: entry.title, seenByParts: entry.seenByParts }}
                  />
                )}
                <MaterialToggle reference={{ entryId: entry.id, field: f.key }} label={`Feld ${f.label.de}`} />
              </dt>
              <dd>
                <FieldValue field={f} spec={specs.find((s) => s.key === f.key)} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}

/** Edits the fields in one form; only changed fields are sent, so fields the view cannot see stay untouched. */
function FieldsForm(props: { entry: CampaignEntry; specs: CampaignFieldSpec[]; onDone: () => void }) {
  const campaign = useCampaign();
  const entries = useEntries();
  const initial = Object.fromEntries(props.specs.map((s) => [s.key, props.entry.fields.find((f) => f.key === s.key)?.value ?? null]));
  const [values, setValues] = useState<Record<string, unknown>>(initial);
  const action = useAction();
  const set = (key: string, value: unknown) => setValues((v) => ({ ...v, [key]: value }));
  const changedSpecs = props.specs.filter((s) => JSON.stringify(values[s.key] ?? null) !== JSON.stringify(initial[s.key] ?? null));
  useDraft(changedSpecs.length > 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const changed = Object.fromEntries(changedSpecs.map((s) => [s.key, normalize(s, values[s.key])]));
    if (Object.keys(changed).length === 0) {
      props.onDone();
      return;
    }
    if (await action.run(() => campaign.apply({ type: "campaign.entry.update", payload: { id: props.entry.id, fields: changed } }))) props.onDone();
  };

  return (
    <form className="card stack" onSubmit={(e) => void submit(e)} aria-label="Felder">
      {props.specs.map((spec) => {
        const value = values[spec.key];
        const label = spec.label.de;
        switch (spec.type) {
          case "choice":
            return (
              <label key={spec.key} className="field">
                <span>{label}</span>
                <select value={typeof value === "string" ? value : ""} onChange={(e) => set(spec.key, e.target.value || null)}>
                  <option value="">–</option>
                  {spec.choices.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label.de}
                    </option>
                  ))}
                </select>
              </label>
            );
          case "day":
            return (
              <label key={spec.key} className="field">
                <span>{label}</span>
                <input type="number" step={1} value={typeof value === "number" ? value : ""} onChange={(e) => set(spec.key, e.target.value === "" ? null : Number(e.target.value))} />
              </label>
            );
          case "date":
            return (
              <label key={spec.key} className="field">
                <span>{label}</span>
                <input type="date" value={typeof value === "string" ? value : ""} onChange={(e) => set(spec.key, e.target.value || null)} />
              </label>
            );
          case "entry":
            return (
              <label key={spec.key} className="field">
                <span>{label}</span>
                <select value={typeof value === "string" ? value : ""} onChange={(e) => set(spec.key, e.target.value || null)}>
                  <option value="">–</option>
                  {entries.data
                    ?.filter((e) => e.id !== props.entry.id)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.title} ({campaign.kindLabel(e.kind)})
                      </option>
                    ))}
                </select>
              </label>
            );
          case "partyMembers": {
            const selected = Array.isArray(value) ? (value as string[]) : [];
            return (
              <fieldset key={spec.key} className="field">
                <legend className="muted small">{label}</legend>
                {campaign.head.party.map((p) => (
                  <label key={p.id} className="option">
                    <input
                      type="checkbox"
                      checked={selected.includes(p.id)}
                      onChange={(e) => set(spec.key, e.target.checked ? [...selected, p.id] : selected.filter((id) => id !== p.id))}
                    />
                    {p.name}
                  </label>
                ))}
              </fieldset>
            );
          }
          case "references": {
            const items = Array.isArray(value) ? (value as { entryId: string }[]) : [];
            return (
              <fieldset key={spec.key} className="field">
                <legend className="muted small">{label}</legend>
                <ul className="plain-list">
                  {items.map((item, i) => (
                    <li key={i} className="row">
                      <span>{entries.data?.find((e) => e.id === item.entryId)?.title ?? "…"}</span>
                      <button type="button" className="link danger" onClick={() => set(spec.key, items.filter((_, j) => j !== i))}>
                        Entfernen
                      </button>
                    </li>
                  ))}
                </ul>
                <select
                  value=""
                  aria-label={`${label} ergänzen`}
                  onChange={(e) => e.target.value && set(spec.key, [...items, { entryId: e.target.value }])}
                >
                  <option value="">Eintrag ergänzen …</option>
                  {entries.data?.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title}
                    </option>
                  ))}
                </select>
              </fieldset>
            );
          }
          default:
            return (
              <label key={spec.key} className="field">
                <span>{label}</span>
                <textarea rows={2} value={typeof value === "string" ? value : ""} onChange={(e) => set(spec.key, e.target.value)} />
              </label>
            );
        }
      })}
      <CampaignError error={action.error} onClose={action.clear} />
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Speichern
        </button>
        <button type="button" className="button secondary" onClick={props.onDone}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

/**
 * An empty text is sent as no value, so the field is cleared. An empty list stays an empty list, not no value: a list whose
 * targets the view cannot see arrives empty as well, and on saving the server keeps those hidden targets.
 */
function normalize(spec: CampaignFieldSpec, value: unknown): unknown {
  if (value == null) return null;
  if (spec.type === "text" && typeof value === "string" && value.trim() === "") return null;
  return value;
}

function SectionCard(props: { entry: CampaignEntry; section: CampaignSection; index: number; count: number }) {
  const { entry, section } = props;
  const campaign = useCampaign();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [heading, setHeading] = useState(section.heading);
  const [text, setText] = useState(section.text);
  const action = useAction();
  useDraft(editing && (heading !== section.heading || text !== section.text));

  // The form starts from the section as it is now, after an undo or a change elsewhere.
  const startEditing = () => {
    setHeading(section.heading);
    setText(section.text);
    action.clear();
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = { id: section.id, text };
    if (heading !== section.heading) payload.heading = heading;
    if (await action.run(() => campaign.apply({ type: "campaign.section.update", payload }))) setEditing(false);
  };
  const move = (position: number) => action.run(() => campaign.apply({ type: "campaign.section.move", payload: { id: section.id, position } }));

  return (
    <section className={`card section${section.visibility === "secret" ? " secret" : ""}`} aria-label={section.heading}>
      {!editing ? (
        <>
          <div className="section-head">
            <h2>{section.heading}</h2>
            <div className="actions">
              <VisibilityToggle
                visibility={section.visibility}
                label={`Abschnitt ${section.heading}`}
                onChange={(visibility) => action.run(() => campaign.apply({ type: "campaign.visibility", payload: { target: "section", id: section.id, visibility } }))}
              />
              {campaign.canWrite && section.visibility === "secret" && (
                <RevealButton
                  reveal={{ target: "section", id: section.id, label: `Abschnitt „${section.heading}“`, entryTitle: entry.title, seenByParts: entry.seenByParts }}
                />
              )}
              <MaterialToggle reference={{ entryId: entry.id, sectionId: section.id }} label={`Abschnitt ${section.heading}`} />
              {campaign.canWrite && (
                <>
                  <button type="button" className="icon-button" aria-label={`${section.heading} nach oben`} disabled={props.index === 0} onClick={() => void move(props.index - 1)}>
                    ↑
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`${section.heading} nach unten`}
                    disabled={props.index === props.count - 1}
                    onClick={() => void move(props.index + 2)}
                  >
                    ↓
                  </button>
                  <button type="button" className="button secondary" onClick={startEditing}>
                    Bearbeiten
                  </button>
                  {!section.key && (
                    <button type="button" className="link danger" onClick={() => setDeleting(true)}>
                      Löschen
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {section.text.trim() ? (
            <Markdown text={section.text} references={section.references} entryPath={campaign.entryPath} />
          ) : (
            <p className="muted small">Noch leer.</p>
          )}
        </>
      ) : (
        <form className="stack" onSubmit={(e) => void save(e)}>
          <label className="field">
            <span>Überschrift</span>
            <input value={heading} onChange={(e) => setHeading(e.target.value)} disabled={!!section.key} />
          </label>
          <label className="field">
            <span>Text (Markdown, Verweise als [[Titel]])</span>
            <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          </label>
          <div className="actions">
            <button type="submit" className="button primary" disabled={action.busy}>
              Speichern
            </button>
            <button type="button" className="button secondary" onClick={() => setEditing(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
      {deleting && (
        <ConfirmDialog
          title="Abschnitt löschen"
          action="Löschen"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={() => campaign.apply({ type: "campaign.section.delete", payload: { id: section.id } })}
        >
          <p>
            Der Abschnitt „{section.heading}“ in „{entry.title}“ wird gelöscht.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function NewSection({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const [open, setOpen] = useState(false);
  const [heading, setHeading] = useState("");
  const [text, setText] = useState("");
  const action = useAction();
  useDraft(open && (heading.trim() !== "" || text.trim() !== ""));
  if (!open)
    return (
      <div className="actions">
        <button type="button" className="button secondary" onClick={() => setOpen(true)}>
          Abschnitt hinzufügen
        </button>
      </div>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await action.run(async () => {
      if (!heading.trim()) throw new Error("Der Abschnitt braucht eine Überschrift.");
      await campaign.apply({ type: "campaign.section.create", payload: { entryId: entry.id, heading, text } });
    });
    if (ok) {
      setHeading("");
      setText("");
      setOpen(false);
    }
  };
  return (
    <form className="card stack" onSubmit={(e) => void submit(e)} aria-label="Neuer Abschnitt">
      <label className="field">
        <span>Überschrift</span>
        <input value={heading} onChange={(e) => setHeading(e.target.value)} autoFocus />
      </label>
      <label className="field">
        <span>Text (Markdown, Verweise als [[Titel]])</span>
        <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <p className="muted small">{campaign.mode === "dossier" ? "Im Dossier angelegt ist der Abschnitt geheim." : "Der Abschnitt ist bekannt."}</p>
      <CampaignError error={action.error} onClose={action.clear} />
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Anlegen
        </button>
        <button type="button" className="button secondary" onClick={() => setOpen(false)}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function EntryLinks({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const entries = useEntries();
  const [relation, setRelation] = useState(campaign.head.relations[0]?.id ?? "related");
  const [target, setTarget] = useState("");
  const action = useAction();
  const characters = campaign.head.party.filter((p) => p.characterId);
  if (entry.links.length === 0 && !campaign.canWrite) return null;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const [type, id] = target.split(":");
    if (!id) return;
    const payload = type === "c" ? { fromEntryId: entry.id, toCharacterId: id, relation } : { fromEntryId: entry.id, toEntryId: id, relation };
    if (await action.run(() => campaign.apply({ type: "campaign.link.add", payload }))) setTarget("");
  };

  return (
    <section className="card">
      <h2>Verknüpfungen</h2>
      {entry.links.length > 0 && (
        <ul className="plain-list" aria-label="Verknüpfungen">
          {entry.links.map((link) => (
            <li key={link.id} className="row">
              <span className="muted small">{link.label.de}</span>
              {link.entryId ? <Link to={campaign.entryPath(link.entryId)}>{link.title}</Link> : <span>{link.title}</span>}
              <VisibilityToggle
                visibility={link.visibility}
                label={`Verknüpfung ${link.title}`}
                onChange={(visibility) => action.run(() => campaign.apply({ type: "campaign.visibility", payload: { target: "link", id: link.id, visibility } }))}
              />
              {campaign.mode === "dossier" && link.visibility === "secret" && (
                <RevealButton reveal={{ target: "link", id: link.id, label: `Verknüpfung „${link.title}“`, entryTitle: entry.title }} />
              )}
              {campaign.canWrite && (
                <button type="button" className="link danger" onClick={() => void action.run(() => campaign.apply({ type: "campaign.link.remove", payload: { id: link.id } }))}>
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {campaign.canWrite && (
        <form className="inline-form two" onSubmit={(e) => void add(e)} aria-label="Neue Verknüpfung">
          <label className="field">
            <span>Art</span>
            <select value={relation} onChange={(e) => setRelation(e.target.value)}>
              {campaign.head.relations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label.de}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Ziel</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">–</option>
              {entries.data
                ?.filter((e) => e.id !== entry.id)
                .map((e) => (
                  <option key={e.id} value={`e:${e.id}`}>
                    {e.title} ({campaign.kindLabel(e.kind)})
                  </option>
                ))}
              {characters.map((p) => (
                <option key={p.id} value={`c:${p.characterId}`}>
                  {p.name} ({campaign.term("member")})
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="button secondary" disabled={!target || action.busy}>
            Verknüpfen
          </button>
        </form>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}

function EntryAttachments({ entry }: { entry: CampaignEntry }) {
  const campaign = useCampaign();
  const input = useRef<HTMLInputElement>(null);
  const [sectionId, setSectionId] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const action = useAction();
  if (entry.attachments.length === 0 && !campaign.canWrite) return null;

  const uploadFile = async (file: File) => {
    setError(null);
    if (file.size > maxAttachmentBytes) {
      setError(new Error(`„${file.name}“ ist ${Math.ceil(file.size / (1024 * 1024))} MB groß. Ein Anhang darf höchstens 20 MB groß sein.`));
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("entryId", entry.id);
      if (sectionId) form.append("sectionId", sectionId);
      form.append("expectedRevision", String(campaign.head.revision));
      const response = await campaign.fetch(`/api/campaigns/${campaign.id}/attachments`, { method: "POST", body: form });
      const body = (await response.json().catch(() => ({}))) as { title?: string };
      if (response.status === 409) throw new ApiError(409, conflictMessage);
      if (!response.ok) throw new ApiError(response.status, body.title ?? `Fehler ${response.status}`);
      campaign.reload();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <section className="card">
      <h2>Anhänge</h2>
      {entry.attachments.length > 0 && (
        <ul className="plain-list" aria-label="Anhänge">
          {entry.attachments.map((a) => (
            <li key={a.id} className="row">
              <AttachmentButton attachment={a} />
              {a.sectionId && <span className="muted small">· {entry.sections.find((s) => s.id === a.sectionId)?.heading}</span>}
              <VisibilityToggle
                visibility={a.visibility}
                label={`Anhang ${a.name}`}
                onChange={(visibility: Visibility) => action.run(() => campaign.apply({ type: "campaign.visibility", payload: { target: "attachment", id: a.id, visibility } }))}
              />
              {campaign.canWrite && a.visibility === "secret" && (
                <RevealButton reveal={{ target: "attachment", id: a.id, label: `Anhang „${a.name}“`, entryTitle: entry.title, seenByParts: entry.seenByParts }} />
              )}
              <MaterialToggle reference={{ entryId: entry.id, attachmentId: a.id }} label={`Anhang ${a.name}`} />
              {campaign.canWrite && (
                <button type="button" className="link danger" onClick={() => void action.run(() => campaign.apply({ type: "campaign.attachment.remove", payload: { id: a.id } }))}>
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {campaign.canWrite && (
        <div className="inline-form two">
          {entry.sections.length > 0 && (
            <label className="field">
              <span>Gehört zu</span>
              <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                <option value="">dem ganzen Eintrag</option>
                {entry.sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    Abschnitt „{s.heading}“
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="button secondary" disabled={busy} onClick={() => input.current?.click()}>
            Datei hochladen
          </button>
          <input ref={input} type="file" hidden aria-label="Anhang" onChange={(e) => e.target.files?.[0] && void uploadFile(e.target.files[0])} />
          <p className="muted small">Höchstens 20 MB je Datei.</p>
        </div>
      )}
      <ErrorNote error={error} />
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}
